import { FC, ReactNode, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { formatDateToPtBr, OptionsHelper } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  FetchState,
  FormDateInput,
  FormInput,
  FormSelect,
  FormTextArea,
  IOption,
  ListLoading,
  useModal,
  useToast,
} from 'app/components'
import { ErroDeCarga } from '../../core/ErroDeCarga'
import {
  DispositivoGestaoView,
  SituacaoDispositivo,
  TipoDispositivoGestao,
} from '../../core/_models'
import { createDispositivoGestao, updateDispositivoGestao } from '../../core/_requests'
import {
  opcoesClasseRisco,
  opcoesMotivoBaixa,
  opcoesRegime,
  opcoesSituacao,
  opcoesTipo,
  rotuloMaleta,
} from '../../core/rotulos'
import {
  paraDto,
  periodicidadeSugerida,
  valoresIniciais,
  ValoresDispositivo,
} from '../../core/formularioDispositivo'
import { trataErroDoModal } from '../../core/errosFormulario'
import { buscaOpcoesFornecedores } from '../../core/opcoes'
import { dispositivoSchema } from './validation/dispositivoSchema'
import { ErrosGerais } from '../../core/ErrosGerais'
import { RodapeModal } from '../../core/RodapeModal'

const buscaFornecedoresAtivos = buscaOpcoesFornecedores(true)

/** Campos do formulário que recebem o 422 por campo. */
const CAMPOS_FORM = [
  'empresa',
  'tipo',
  'serie',
  'fabricante',
  'modelo',
  'patrimonio',
  'macAddress',
  'firmware',
  'registroAnvisa',
  'registroAnvisaValidade',
  'classeRisco',
  'certificacaoAnatel',
  'certificacaoInmetro',
  'lote',
  'udiDi',
  'udiPi',
  'fornecedor',
  'notaFiscal',
  'dtAquisicao',
  'garantiaAte',
  'regime',
  'periodicidadeCalibracaoMeses',
  'dtUltimaCalibracao',
  'situacao',
  'motivoBaixa',
  'dtBaixa',
  'destinoBaixa',
  'observacoes',
] as const

const MAPA_CAMPOS = { idEmpresa: 'empresa', idFornecedor: 'fornecedor' }

const Secao = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <fieldset className='mb-8'>
    <legend className='fs-6 fw-semibold text-gray-900 mb-4'>{titulo}</legend>
    <div className='row g-4'>{children}</div>
  </fieldset>
)

const Motivo = ({ id, children }: { id: string; children: ReactNode }) => (
  <div id={id} className='form-text text-gray-700'>
    {children}
  </div>
)

/** Cadastro/edição na gestão. Na edição, espera o fetcher `dispositivo` no `showModal`. */
export const DispositivoModal: FC = () => {
  const { fetchStates } = useModal()
  const estado = fetchStates['dispositivo'] as FetchState<DispositivoGestaoView> | undefined
  // O formulário só monta com o cadastro carregado: os valores iniciais vêm dele.
  if (estado?.error) return <ErroDeCarga error={estado.error} />
  if (estado && (estado.isLoading || !estado.data)) return <ListLoading />
  return <FormularioDispositivo dispositivo={estado?.data} />
}

const FormularioDispositivo: FC<{ dispositivo?: DispositivoGestaoView }> = ({ dispositivo }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const { t } = useT()
  const [erroGeral, setErroGeral] = useState<string[]>([])
  const edicao = !!dispositivo
  const alocado = !!dispositivo?.maletaAtual
  const vendido = dispositivo?.regime === 'VENDIDO'
  const emUso = dispositivo?.situacao === 'EM_USO'

  const opcoes = useMemo(
    () => ({
      tipo: opcoesTipo(t),
      classeRisco: opcoesClasseRisco(t),
      regime: opcoesRegime(t),
      situacao: opcoesSituacao(t, true),
      motivoBaixa: opcoesMotivoBaixa(t),
    }),
    [t]
  )
  // Valor sugerido da periodicidade: acompanha o tipo enquanto o usuário não editar o campo.
  const ultimaSugestao = useRef(String(periodicidadeSugerida()))

  const formik = useFormik<ValoresDispositivo>({
    initialValues: valoresIniciais(t, dispositivo),
    validationSchema: useMemo(() => dispositivoSchema(t), [t]),
    onSubmit: async (valores, helpers) => {
      setErroGeral([])
      try {
        const dto = paraDto(valores)
        if (dispositivo) await updateDispositivoGestao(dispositivo.id, dto)
        else await createDispositivoGestao(dto)
        showToast({
          variant: 'success',
          title: t(
            edicao ? 'pagina.dispositivos.modal.atualizado' : 'pagina.dispositivos.modal.criado'
          ),
        })
        closeModal()
      } catch (error) {
        trataErroDoModal(error, helpers, CAMPOS_FORM, setErroGeral, MAPA_CAMPOS)
      }
    },
  })

  const { setFieldValue } = formik
  const situacaoAtual = formik.values.situacao?.value
  const baixado = situacaoAtual === 'BAIXADO'

  const aoTrocarTipo = (valor: IOption | IOption[] | null) => {
    if (edicao) return
    const tipo = (valor as IOption<TipoDispositivoGestao> | null)?.value
    const sugerida = String(periodicidadeSugerida(tipo))
    if (formik.values.periodicidadeCalibracaoMeses === ultimaSugestao.current) {
      setFieldValue('periodicidadeCalibracaoMeses', sugerida)
    }
    ultimaSugestao.current = sugerida
  }

  const aoTrocarSituacao = (valor: IOption | IOption[] | null) => {
    const nova = (valor as IOption<SituacaoDispositivo> | null)?.value
    if (nova !== 'BAIXADO') {
      setFieldValue('motivoBaixa', undefined)
      setFieldValue('dtBaixa', '')
      setFieldValue('destinoBaixa', '')
    }
  }

  const motivoEmpresa = alocado
    ? t('pagina.dispositivos.modal.motivo_empresa_alocado')
    : vendido
    ? t('pagina.dispositivos.modal.motivo_empresa_vendido')
    : undefined

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <ErrosGerais erros={erroGeral} />

      <Secao titulo={t('pagina.dispositivos.secao.identificacao')}>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='empresa'
            label={t('pagina.dispositivos.campo.empresa')}
            inputId='dispositivo-empresa'
            fetchOptions={OptionsHelper.getOptionsEmpresas}
            fetchOptionsParams={{ onlyActive: false }}
            isClearable={false}
            isRequired
            isDisabled={!!motivoEmpresa}
          />
          {motivoEmpresa && <Motivo id='dispositivo-empresa-motivo'>{motivoEmpresa}</Motivo>}
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='tipo'
            label={t('pagina.dispositivos.campo.tipo')}
            inputId='dispositivo-tipo'
            options={opcoes.tipo}
            isClearable={false}
            isRequired
            isDisabled={alocado}
            onChange={aoTrocarTipo}
          />
          {alocado && (
            <Motivo id='dispositivo-tipo-motivo'>
              {t('pagina.dispositivos.modal.motivo_tipo_alocado')}
            </Motivo>
          )}
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='serie'
            label={t('pagina.dispositivos.campo.serie')}
            isRequired
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='fabricante'
            label={t('pagina.dispositivos.campo.fabricante')}
          />
        </div>
        <div className='col-md-4'>
          <FormInput formik={formik} name='modelo' label={t('pagina.dispositivos.campo.modelo')} />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='patrimonio'
            label={t('pagina.dispositivos.campo.patrimonio')}
            infoText={t('pagina.dispositivos.ajuda.patrimonio')}
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='macAddress'
            label={t('pagina.dispositivos.campo.mac')}
            placeholder='AA:BB:CC:DD:EE:FF'
            infoText={t('pagina.dispositivos.ajuda.mac')}
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='firmware'
            label={t('pagina.dispositivos.campo.firmware')}
          />
        </div>
      </Secao>

      <Secao titulo={t('pagina.dispositivos.secao.regulatorio')}>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='registroAnvisa'
            label={t('pagina.dispositivos.campo.registro_anvisa')}
          />
        </div>
        <div className='col-md-4'>
          <FormDateInput
            formik={formik}
            name='registroAnvisaValidade'
            label={t('pagina.dispositivos.campo.registro_anvisa_validade')}
          />
        </div>
        <div className='col-md-4'>
          <FormSelect
            formik={formik}
            name='classeRisco'
            label={t('pagina.dispositivos.campo.classe_risco')}
            inputId='dispositivo-classe-risco'
            options={opcoes.classeRisco}
            isClearable
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='certificacaoAnatel'
            label={t('pagina.dispositivos.campo.certificacao_anatel')}
          />
        </div>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='certificacaoInmetro'
            label={t('pagina.dispositivos.campo.certificacao_inmetro')}
          />
        </div>
        <div className='col-md-4'>
          <FormInput formik={formik} name='lote' label={t('pagina.dispositivos.campo.lote')} />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='udiDi' label={t('pagina.dispositivos.campo.udi_di')} />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='udiPi' label={t('pagina.dispositivos.campo.udi_pi')} />
        </div>
      </Secao>

      <Secao titulo={t('pagina.dispositivos.secao.aquisicao')}>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='fornecedor'
            label={t('pagina.dispositivos.campo.fornecedor')}
            inputId='dispositivo-fornecedor'
            fetchOptions={buscaFornecedoresAtivos}
            filtraNoServidor
            isClearable
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='notaFiscal'
            label={t('pagina.dispositivos.campo.nota_fiscal')}
          />
        </div>
        <div className='col-md-4'>
          <FormDateInput
            formik={formik}
            name='dtAquisicao'
            label={t('pagina.dispositivos.campo.dt_aquisicao')}
          />
        </div>
        <div className='col-md-4'>
          <FormDateInput
            formik={formik}
            name='garantiaAte'
            label={t('pagina.dispositivos.campo.garantia_ate')}
          />
        </div>
        <div className='col-md-4'>
          <FormSelect
            formik={formik}
            name='regime'
            label={t('pagina.dispositivos.campo.regime')}
            inputId='dispositivo-regime'
            options={opcoes.regime}
            isClearable={false}
            isDisabled={vendido}
          />
          {vendido && (
            <Motivo id='dispositivo-regime-motivo'>
              {t('pagina.dispositivos.modal.motivo_regime_vendido')}
            </Motivo>
          )}
        </div>
      </Secao>

      <Secao titulo={t('pagina.dispositivos.secao.calibracao')}>
        <div className='col-md-4'>
          <FormInput
            formik={formik}
            name='periodicidadeCalibracaoMeses'
            type='number'
            label={t('pagina.dispositivos.campo.periodicidade')}
            infoText={edicao ? t('pagina.dispositivos.ajuda.periodicidade') : undefined}
          />
          {!edicao && (
            <Motivo id='dispositivo-periodicidade-ajuda'>
              {t('pagina.dispositivos.ajuda.periodicidade')}
            </Motivo>
          )}
        </div>
        <div className='col-md-4'>
          <FormDateInput
            formik={formik}
            name='dtUltimaCalibracao'
            label={t('pagina.dispositivos.campo.dt_ultima_calibracao')}
          />
        </div>
        {edicao && (
          <div className='col-md-4'>
            <div className='fs-7 fw-bolder mb-2'>
              {t('pagina.dispositivos.campo.dt_proxima_calibracao')}
            </div>
            <div className='text-gray-900'>
              {dispositivo?.dtProximaCalibracao
                ? formatDateToPtBr(dispositivo.dtProximaCalibracao)
                : '—'}
            </div>
          </div>
        )}
      </Secao>

      <Secao titulo={t('pagina.dispositivos.secao.situacao')}>
        <div className='col-md-6'>
          {emUso ? (
            <>
              <div className='fs-7 fw-bolder mb-2'>{t('pagina.dispositivos.campo.situacao')}</div>
              <div className='text-gray-900'>
                {t('pagina.dispositivos.modal.em_uso_na', {
                  maleta: rotuloMaleta(t, dispositivo?.maletaAtual),
                })}
              </div>
              <Motivo id='dispositivo-situacao-motivo'>
                {t('pagina.dispositivos.modal.motivo_situacao_em_uso')}
              </Motivo>
            </>
          ) : (
            <FormSelect
              formik={formik}
              name='situacao'
              label={t('pagina.dispositivos.campo.situacao')}
              inputId='dispositivo-situacao'
              options={opcoes.situacao}
              isClearable={false}
              isRequired
              onChange={aoTrocarSituacao}
            />
          )}
        </div>
        {baixado && (
          <>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='motivoBaixa'
                label={t('pagina.dispositivos.campo.motivo_baixa')}
                inputId='dispositivo-motivo-baixa'
                options={opcoes.motivoBaixa}
                isClearable={false}
                isRequired
              />
              {formik.values.motivoBaixa?.value === 'VENDA' && (
                <Motivo id='dispositivo-venda-aviso'>
                  {t('pagina.dispositivos.modal.aviso_venda')}
                </Motivo>
              )}
            </div>
            <div className='col-md-6'>
              <FormDateInput
                formik={formik}
                name='dtBaixa'
                label={t('pagina.dispositivos.campo.dt_baixa')}
              />
            </div>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='destinoBaixa'
                label={t('pagina.dispositivos.campo.destino_baixa')}
              />
            </div>
          </>
        )}
      </Secao>

      <FormTextArea
        formik={formik}
        name='observacoes'
        label={t('pagina.dispositivos.campo.observacoes')}
        rows={3}
      />

      <RodapeModal enviando={formik.isSubmitting} rotuloEnviar={t('common.save')} />
    </form>
  )
}
